import {ContactRelationship, SchoolClass} from "models"
import {memo, PropTypes, propTypesExact, ShapeComponent, shapeComponent} from "shared/base"
import React, {useRef} from "react"
import Card from "components/utils/card"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import Heading from "components/utils/heading"
import Modal from "components/modal"
import TeachersSelect from "components/user/admin/school-classes/edit/teachers-select"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import UtilsButton from "components/utils/button"
import UtilsInput from "components/utils/input"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

/**
 * @typedef {object} RoutesUserAdminSchoolClassesRolesEditProps
 * @property {unknown} onRequestClose
 * @property {unknown} schoolClass
 */

/**
 * @typedef {object} RoutesUserAdminSchoolClassesRolesEditState
 * @property {boolean} createNewTeacher
 * @property {unknown} teacherId
 */

/**
 * @extends {ShapeComponent<RoutesUserAdminSchoolClassesRolesEditProps, RoutesUserAdminSchoolClassesRolesEditState>}
 */
class RoutesUserAdminSchoolClassesRolesEdit extends ShapeComponent {
  /** @type {RoutesUserAdminSchoolClassesRolesEditState} */
  state = {
    createNewTeacher: false,
    teacherId: undefined
  }

  static propTypes = propTypesExact({
    onRequestClose: PropTypes.func.isRequired,
    schoolClass: PropTypes.instanceOf(SchoolClass).isRequired
  })

  setup() {
    const {t} = useI18n({namespace: "js.routes.user.admin.school_classes.roles.edit"})
    const params = useParams()
    const {contactRelationship} = useModel(ContactRelationship, {
      match: {params},
      newIfNoId: true
    })
    const formRef = useRef()

    this.contactRelationship = contactRelationship
    this.formRef = formRef
    this.t = t
  }

  render() {
    const {contactRelationship, formRef, t} = this.tt
    const {onRequestClose, schoolClass} = this.p
    const form = formRef.current
    const teacherEmail = form?.getValue("school_class_role[user_attributes][email]")
    const teacherFirstName = form?.getValue("school_class_role[user_attributes][first_name]")
    const teacherLastName = form?.getValue("school_class_role[user_attributes][last_name]")

    return (
      <Modal onRequestClose={onRequestClose} testID="user/edit-school-class-role-modal">
        {schoolClass && contactRelationship &&
          <Card style={this.cardStyle ||= {maxWidth: 490, marginLeft: "auto", marginRight: "auto"}}>
            <Form formObjectRef={this.tt.formRef}>
              <Heading>
                {this.headerTitle()}
              </Heading>
              {schoolClass?.school() &&
                <TeachersSelect id="teacher_id" label={t(".assign_teachers")} onChange={this.tt.onTeacherChanged} school={schoolClass.school()} />
              }
              {this.s.createNewTeacher &&
                <View style={this.inputsContainerViewStyle ||= {marginTop: 10}}>
                  <UtilsInput
                    defaultValue={teacherFirstName}
                    label={t(".first_name_of_teacher")}
                    name="school_class_role[user_attributes][first_name]"
                    style={this.teacherFirstNameInputStyle ||= {marginBottom: 10}}
                    testID="teacher-first-name-input"
                  />
                  <UtilsInput
                    defaultValue={teacherLastName}
                    label={t(".last_name_of_teacher")}
                    name="school_class_role[user_attributes][last_name]"
                    style={this.teacherLastNameInputStyle ||= {marginBottom: 10}}
                    testID="teacher-last-name-input"
                  />
                  <UtilsInput
                    defaultValue={teacherEmail}
                    label={t(".email_of_teacher")}
                    name="school_class_role[user_attributes][email]"
                    style={this.emailInputStyle ||= {marginBottom: 10}}
                    testID="teacher-email-input"
                  />
                </View>
              }
              <UtilsButton
                onPress={this.tt.onSaveClassPressed}
                style={this.saveButtonStyle ||= {marginTop: 10}}
                testID="save-school-class-role-button"
                title={this.s.createNewTeacher ? t(".invite_teacher_and_assign_to_class") : t(".assign_teacher_to_class")}
              />
            </Form>
          </Card>
        }
      </Modal>
    )
  }

  headerTitle() {
    if (this.tt.contactRelationship?.isNewRecord()) {
      return this.t(".add_teacher_to_class")
    } else if (this.tt.contactRelationship?.isPersisted()) {
      return this.t(".edit_role_on_class")
    }
  }

  onTeacherChanged = ({options}) => {
    const option = options[0]

    if (option?.optionType == "teacher") {
      this.setState({
        createNewTeacher: false,
        teacherId: option.value
      })
    } else if (option?.optionType == "create-new-teacher") {
      this.setState({
        createNewTeacher: true,
        teacherId: null
      })
    }
  }

  onSaveClassPressed = async () => {
    const {t} = this.tt
    const {schoolClass} = this.p

    try {
      const isNewSchoolClassRole = this.tt.contactRelationship.isNewRecord()
      const saveData = this.tt.formRef.current.asObject()

      saveData.school_class_role ||= {}
      saveData.school_class_role.role = "teacher"
      saveData.school_class_role.school_class_id = schoolClass.id()

      if (this.s.teacherId) {
        saveData.school_class_role.user_id = this.s.teacherId
      } else if (this.s.createNewTeacher) {
        const password = uuidv4()
        const userAttributes = saveData.school_class_role.user_attributes || {}

        saveData.school_class_role.user_attributes = {
          ...userAttributes,
          password,
          password_confirmation: password
        }
      } else {
        FlashMessage.alert(t(".you_must_choose_an_existing_user_or_create_a_new_one"))

        return
      }

      await ContactRelationship.schoolLeaderCreate(saveData)
      FlashMessage.success(isNewSchoolClassRole ? t(".the_role_was_created") : t(".the_role_was_updated"))
      this.p.onRequestClose()
    } catch (e) {
      FlashMessage.errorResponse(e)
    }
  }
}

export default memo(shapeComponent(RoutesUserAdminSchoolClassesRolesEdit))
